import {
  Asset,
  FeatureName,
  GrippContext,
  IssueCreationSource,
  RoleKey,
  RoleNameToKeyMap,
  useCurrentAccount,
  useIsFeatureEnabled,
  useStorageKeys,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  ArrowTopRightAngleIcon,
  BaseTheme,
  BottomComponentBar,
  ImageComponent,
  ImageData,
  KeyboardSafeView,
  Layout,
  Link,
  LocationIcon,
  RoutineButtons,
  RoutineExecutionProps,
  RoutineModal,
  SizePrefix,
  tokens,
  useTypedMediaItem,
} from '@gripp/shared-ui';
import { useState } from 'react';
import {
  Linking,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { PaperProvider, Text } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { SignatureScreenWeb } from '../../src/shared/components/signature/signatureScreenWeb';
import { MediaPicker } from '../shared/components/media/mediaPicker';
import { localStorageHandler } from '../shared/localStorageHandler';
import { SentryWebLogger } from '../shared/logging/sentryWebLogger';
import { ActionButtons } from './actionButtons';
import { AnonymousDrop, DropVariant } from './anonymousDrop';
import { AnonymousIssue } from './anonymousIssue';
import { AnonymousRoutine } from './anonymousRoutine';
import { GetGrippBanner } from './getGrippBanner';

type AnonymousAssetProps = {
  asset: Asset;
  tagCode: string;
};

// The "Get Gripp" banner only makes sense on a mobile device — that's where
// the app can be installed. Mirrors the user-agent check used in invitation.tsx.
const isMobileDevice =
  typeof navigator !== 'undefined' &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

// Opens the asset's last known location in Google Maps (new browser tab).
// Prefers GeoJSON coordinates [lng, lat]; falls back to the address string.
const openAssetLocation = (asset: Asset) => {
  const coordinates = asset.lastLocation?.coordinates;
  const query = coordinates
    ? `${coordinates[1]},${coordinates[0]}`
    : asset.lastAddress;
  if (!query) return;
  Linking.openURL(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      query
    )}`
  );
};

export const AnonymousAsset = (props: AnonymousAssetProps) => {
  const [showReported, toggleShowReported] = useState<boolean>(false);
  const { workspaceAsset } = useWorkspaceAsset(props.asset.id);
  const [routineExecutionProps, setRoutineExecutionProps] = useState<
    RoutineExecutionProps | undefined
  >();
  const [showIssueForm, setShowIssueForm] = useState(false);
  // The in-page routine/report views are the Crop Circle treatment. When CC is
  // off, the flow falls back to the legacy modals (unchanged behavior).
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const storageKeys = useStorageKeys();
  const [anonymousIssueIds, setAnonymousIssueIds] = useState<string[]>([]);
  const [completedVariant, setCompletedVariant] =
    useState<DropVariant>('report');

  // The workspaceAsset query returns the cover image Media without urls, so
  // resolve the displayable url from its id. `coverImageUri` is undefined when
  // the asset has no cover image — ImageComponent then shows its placeholder.
  const { typedMediaItem: coverImage } = useTypedMediaItem<ImageData>(
    workspaceAsset?.asset?.coverImage
  );
  const coverImageUri = coverImage?.urlsBySize?.[SizePrefix.small];

  // Workspace admins and general users may assign routines; everyone else
  // (restricted roles, and anonymous visitors with no account) only gets the
  // read-only routine list.
  const { account } = useCurrentAccount();
  const canAddRoutines =
    !!account &&
    (account.isGlobalAdmin() ||
      (account.roles ?? []).some((role) => {
        const key = RoleNameToKeyMap[role.name];
        return key === RoleKey.WORKSPACE_ADMIN || key === RoleKey.GENERAL;
      }));

  const onIssueCompleted = () => {
    setCompletedVariant('report');
    const loadIssueIds = async () => {
      const stored = await localStorageHandler.getItem(
        storageKeys.anonymousIssueIds
      );
      if (stored) setAnonymousIssueIds(JSON.parse(stored));
    };
    loadIssueIds();
    toggleShowReported(true);
  };

  const onRoutineCompleted = async () => {
    setCompletedVariant('routine');
    toggleShowReported(true);
  };

  function onRoutineSelected(props: RoutineExecutionProps): void {
    setRoutineExecutionProps(props);
  }

  const renderBody = () => {
    if (!workspaceAsset) return null;
    const showBottomBar = !workspaceAsset?.anonymousOptions?.hideReportAndNote;
    return (
      <View style={styles.mainContainer}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={[
            styles.scrollContent,
            showBottomBar && styles.scrollContentWithBottomBar,
          ]}
        >
          <View style={[styles.container, isMobile && styles.containerMobile]}>
            {isMobileDevice && <GetGrippBanner />}
            <View style={styles.asset}>
              <ImageComponent
                source={coverImageUri ? { uri: coverImageUri } : undefined}
                size={196}
                borderRadius={tokens.borderRadius.xs}
                showClose={false}
              />
              <Text style={styles.assetName}>{workspaceAsset.asset.name}</Text>
              {!!workspaceAsset.asset.lastAddress && (
                <Link
                  underline={false}
                  emphasised={false}
                  color="neutral"
                  wrap
                  leftIcon={<LocationIcon size={12} />}
                  rightIcon={<ArrowTopRightAngleIcon size={12} />}
                  textStyle={styles.assetAddressText}
                  accessibilityLabel={workspaceAsset.asset.lastAddress}
                  onPress={() => openAssetLocation(workspaceAsset.asset)}
                >
                  {workspaceAsset.asset.lastAddress}
                </Link>
              )}
            </View>
            <View style={styles.routinesContainer}>
              <RoutineButtons
                workspaceAsset={workspaceAsset}
                onRoutineSelected={onRoutineSelected}
                showAll={true}
                initialRoutinesToShow={3}
                hideAssignRoutinesButton={!canAddRoutines}
              />
            </View>
          </View>
        </ScrollView>
        {showBottomBar && (
          <View style={styles.bottomBarAnchor}>
            <View style={styles.bottomBarConstrained}>
              <BottomComponentBar
                additionalHeaderBlock={
                  <ActionButtons
                    asset={props.asset}
                    onCompleted={onIssueCompleted}
                    onAddNew={() => setShowIssueForm(true)}
                    workspaceId={workspaceAsset.workspace.id}
                  />
                }
              />
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <PaperProvider theme={BaseTheme}>
      <SafeAreaProvider>
        <KeyboardSafeView style={styles.containerKeyboard}>
          <GrippContext.Provider
            value={{
              logger: SentryWebLogger,
              secureStorageHandler: localStorageHandler,
              storageHandler: localStorageHandler,
              mediaPickerComponent: MediaPicker,
              signaturePadScreen: SignatureScreenWeb,
            }}
          >
            {showReported ? (
              <AnonymousDrop
                variant={completedVariant}
                anonymousIssueIds={anonymousIssueIds}
              />
            ) : isCropCircleEnabled && routineExecutionProps ? (
              // CC: replace the legacy RoutineModal (fixed capped height, unreachable
              // submit) with an in-page full-height view, consistent with the
              // authenticated CC asset flow.
              <AnonymousRoutine
                workspaceAsset={routineExecutionProps.workspaceAsset}
                module={routineExecutionProps.module}
                routine={routineExecutionProps.routine}
                state={routineExecutionProps.state}
                additionalState={routineExecutionProps.additionalState}
                onBack={() => setRoutineExecutionProps(undefined)}
                onCompleted={async () => {
                  setRoutineExecutionProps(undefined);
                  await onRoutineCompleted();
                }}
              />
            ) : isCropCircleEnabled && showIssueForm && workspaceAsset ? (
              // CC: in-page report/note form (replaces the legacy NewIssueModal),
              // matching the routine view's back chrome via the shared PanelHeader.
              <AnonymousIssue
                assetId={props.asset.id}
                workspaceId={workspaceAsset.workspace.id}
                onBack={() => setShowIssueForm(false)}
                onCompleted={() => {
                  setShowIssueForm(false);
                  onIssueCompleted();
                }}
              />
            ) : (
              <>
                {renderBody()}
                {/* Non-CC: the legacy routine modal overlays the page (CC uses the
                    in-page view above; report/note keeps its own modal inside the
                    legacy ActionButtons). */}
                {!isCropCircleEnabled && routineExecutionProps && (
                  <RoutineModal
                    {...routineExecutionProps}
                    visible
                    confirmCancel
                    onCanceled={() => setRoutineExecutionProps(undefined)}
                    onCompleted={async () => {
                      setRoutineExecutionProps(undefined);
                      await onRoutineCompleted();
                    }}
                    createdFrom={IssueCreationSource.Anonymousweb}
                  />
                )}
              </>
            )}
          </GrippContext.Provider>
        </KeyboardSafeView>
      </SafeAreaProvider>
    </PaperProvider>
  );
};

// Caps the content column; the floating bottom bar mirrors it so the button
// stays aligned with the routines/asset column on wide screens.
const CONTENT_MAX_WIDTH = 800;

const styles = StyleSheet.create({
  containerKeyboard: {
    ...Layout.container,
  },
  mainContainer: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: tokens.colors.neutral[100],
    width: '100%',
    marginHorizontal: 0,
    paddingHorizontal: 0,
  },
  scrollContainer: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    alignItems: 'center',
  },
  // Reserved space for the floating BottomComponentBar so the last routine
  // isn't hidden beneath it. Only applied when the bar renders.
  scrollContentWithBottomBar: {
    paddingBottom: 110,
  },
  container: {
    minWidth: '60%',
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
    padding: tokens.spacing.lg, // 16
    rowGap: tokens.spacing['4xl'], // 32 between banner / asset / routines
  },
  containerMobile: {
    width: '100%',
    maxWidth: '100%',
  },
  routinesContainer: {
    flex: 1,
  },
  asset: {
    rowGap: tokens.spacing.lg, // 16px between image, name and location
  },
  assetName: {
    ...tokens.typography.headline.smallEmphasised,
    color: tokens.colors.neutral[800],
  },
  assetAddressText: {
    ...tokens.typography.body.small,
    fontWeight: '500',
  },
  // Spans the viewport and centers the constrained bar within it.
  bottomBarAnchor: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1000,
  },
  // Caps the floating bar at the content width so it lines up with the
  // routines/asset column. `position: relative` makes this the containing
  // block for BottomBar's inner `position: absolute, width: 100%`.
  bottomBarConstrained: {
    position: 'relative',
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
  },
});
